// import external libraries
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

// import components
import { GridCompact6 } from 'components/shared/Grid/Grid.components';
import BacklogEntitySearch from '../BacklogEntitySearch';
import BacklogFunctionFilter from '../BacklogFunctionFilter';

// import styles
import { BacklogFilterFieldBox } from '../../../../Backlog.styles';

// import types
import type { BacklogFiltersForm } from '../../BacklogFilters.types';
import type { BacklogEmployeeViewFieldsProps } from './BacklogEmployeeViewFields.types';

/**
 * The employee view's own two fields: the employees it reports on, and the functions.
 *
 * The employee field is the same planning-entity search the patient view uses, narrowed by the picked
 * plan units, which arrive as a prop already resolved. The function field takes none of them: a roster
 * function is a tenant-wide record — see {@link BacklogFunctionFilter}.
 */
const BacklogEmployeeViewFields = ({
  organizationalUnitCostCenterIds,
  startDate,
  endDate,
  scopeMissing
}: BacklogEmployeeViewFieldsProps) => {
  'use memo';

  // The entity type names sit under the mrp namespace, shared with afsprakenplanning's own pickers.
  const { t: tCommon } = useTranslation('common');
  const { control } = useFormContext<BacklogFiltersForm>();

  return (
    <>
      <GridCompact6>
        <BacklogFilterFieldBox>
          <Controller
            name="EMPLOYEE"
            control={control}
            render={({ field }) => (
              <BacklogEntitySearch
                entityType="EMPLOYEE"
                label={tCommon('mrp.entityTypeMap.EMPLOYEE.single')}
                selected={field.value}
                onChange={field.onChange}
                // Marked while neither this nor the plan unit is filled: an employee pick scopes this
                // view on its own.
                required={scopeMissing}
                // With a plan unit picked this opens on that unit's employees as a list instead of
                // asking the planner to type a name.
                organizationalUnitIds={organizationalUnitCostCenterIds}
              />
            )}
          />
        </BacklogFilterFieldBox>
      </GridCompact6>
      <GridCompact6>
        <BacklogFilterFieldBox>
          <Controller
            name="employeeFunctions"
            control={control}
            render={({ field }) => (
              <BacklogFunctionFilter
                startDate={startDate}
                endDate={endDate}
                selected={field.value}
                onChange={field.onChange}
              />
            )}
          />
        </BacklogFilterFieldBox>
      </GridCompact6>
    </>
  );
};

export default BacklogEmployeeViewFields;
